//* Espejo de `api-rendafin/src/modules/clients/constants/contact-types.ts` (plan v4.3 §6.1). La fuente es la API: si
//* cambia allá, se cambia aquí. Solo «Obligado solidario» es contrato; los demás tipos de cada frente todavía difieren.

export const GUARANTOR_CONTACT_TYPE = 'Obligado solidario'

/** Igual que la API: hasta 5 obligados por cliente y al menos uno una vez que se registraron. */
export const MAX_GUARANTORS = 5

export const GUARANTOR_RELATIONSHIPS = [
  { value: 'shareholder', label: 'Socio o accionista' },
  { value: 'legal_representative', label: 'Representante legal' },
  { value: 'spouse', label: 'Cónyuge' },
  { value: 'relative', label: 'Familiar' },
  { value: 'related_company', label: 'Empresa relacionada' },
  { value: 'third_party', label: 'Tercero' },
] as const

export type GuarantorRelationship = (typeof GUARANTOR_RELATIONSHIPS)[number]['value']

//* RFC con Ñ y & (los del SAT los admiten): PF 13 caracteres, PM 12.
export const RFC_PF_PATTERN = /^[A-ZÑ&]{4}\d{6}[A-Z\d]{3}$/
export const RFC_PM_PATTERN = /^[A-ZÑ&]{3}\d{6}[A-Z\d]{3}$/
export const CURP_PATTERN = /^[A-Z]{4}\d{6}[HMX][A-Z]{5}[A-Z\d]\d$/

/** Un obligado solidario como lo devuelve `GET /clients/:id`. Sin RFC ni CURP para los roles que no los ven (§6.4). */
export interface GuarantorContact {
  id: string
  contactType: string
  personType: 'PF' | 'PM'
  relationship: string
  firstName?: string
  lastName?: string
  middleName?: string
  businessName?: string
  rfc?: string
  curp?: string
  phone: string
  email?: string
}

export const relationshipLabel = (value: string | undefined): string => GUARANTOR_RELATIONSHIPS.find((option) => option.value === value)?.label ?? 'Sin relación'

const normalize = (text: unknown) =>
  String(text ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase()

/** Igual que la API: por el tipo, sin acentos ni mayúsculas (los datos viejos varían). */
export function isGuarantorContact(contact: { contactType?: unknown } | null | undefined): boolean {
  return normalize(contact?.contactType) === normalize(GUARANTOR_CONTACT_TYPE)
}

/** Los contactos que no son obligados: los que muestra y edita el formulario del cliente. */
export function withoutGuarantors<T extends { contactType?: unknown }>(contacts: T[] | null | undefined): T[] {
  return (Array.isArray(contacts) ? contacts : []).filter((contact) => !isGuarantorContact(contact))
}

export function guarantorsOf(contacts: unknown): GuarantorContact[] {
  return (Array.isArray(contacts) ? contacts : []).filter((contact) => isGuarantorContact(contact)) as GuarantorContact[]
}

/** Nombre para mostrar: la razón social de una PM, o nombre y apellidos. */
export function guarantorDisplayName(contact: Partial<GuarantorContact> | null | undefined): string {
  if (contact?.personType === 'PM' && contact.businessName?.trim()) return contact.businessName.trim()
  const parts = [contact?.firstName, contact?.lastName, contact?.middleName]
  return (
    parts
      .filter((part): part is string => typeof part === 'string' && !!part.trim())
      .map((part) => part.trim())
      .join(' ') || 'Obligado sin nombre'
  )
}

/**
 * Lo que espera `PUT /clients/:id/guarantors` por obligado: solo los campos de su tipo de persona y sin vacíos (un
 * correo vacío fallaría `@IsEmail`). El `id` se conserva para que la API actualice en vez de crear.
 */
export function toGuarantorPayload(contact: Partial<GuarantorContact>): Record<string, string> {
  const isCompany = contact.personType === 'PM'
  const fields: Record<string, unknown> = {
    id: contact.id,
    personType: contact.personType,
    relationship: contact.relationship,
    firstName: isCompany ? undefined : contact.firstName,
    lastName: isCompany ? undefined : contact.lastName,
    middleName: isCompany ? undefined : contact.middleName,
    businessName: isCompany ? contact.businessName : undefined,
    rfc: contact.rfc?.toUpperCase(),
    curp: isCompany ? undefined : contact.curp?.toUpperCase(),
    phone: contact.phone,
    email: contact.email,
  }
  const payload: Record<string, string> = {}
  for (const [key, value] of Object.entries(fields)) {
    const text = typeof value === 'string' ? value.trim() : ''
    if (text) payload[key] = text
  }
  return payload
}
